<template>
  <div class="products-card-container">
    <ProductCard
      v-bind="productData"
      :pdp-url="pdpUrl"
      :add-to-bag-text="
        isAddingToBag ? $t('carousel.addingToBag') : $t('carousel.addToBag')
      "
      :add-to-waitlist-text="$t('carousel.addToWaitlist')"
      :oos-text="$t('carousel.oos')"
      :waitlisted-text="$t('carousel.addedToWaitlist')"
      :waitlisted-variant-ids="waitlistedVariantIds"
      :wishlisted-variant-ids="wishlistedVariantIds"
      :is-cta-disabled="isAddingToBag || isWaitlisted"
      :is-logged-in="userLoggedIn"
      @add-to-wishlist="addToWishlist"
      @add-to-bag="addToBag"
      @add-to-waitlist="addToWaitlist"
    >
      <template #default="slotProps">
        <a
          :href="slotProps.pdpUrl"
          :aria-label="slotProps.alt"
          class="product-card-image-link"
        >
          <nuxt-img
            class="product-card-image"
            loading="lazy"
            :src="
              slotProps.featuredVariant.imageUrl ||
              slotProps.placeholderVariantImage
            "
            :alt="slotProps.alt"
          />
        </a>
      </template>
    </ProductCard>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ProductCard from '@sephora-asia/core-product-card/ProductCard.vue'

export default {
  name: 'ProductCardContainer',

  components: {
    ProductCard
  },

  props: {
    product: {
      type: Object,
      required: true
    },
    productIndex: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      isAddingToBag: false,
      isWaitlisted: false
    }
  },

  computed: {
    ...mapGetters({
      showVariantTagsOnPlp: 'globalConfig/showVariantTagsOnPlp',
      lineItems: 'cart/lineItems',
      waitlistedVariantIds: 'waitlist/waitlistedVariantIds',
      wishlistedVariantIds: 'wishlist/wishlistedVariantIds',
      userLoggedIn: 'user/loggedIn',
      userCardNumber: 'user/cardNumber',
      userId: 'user/id'
    }),

    productData() {
      const labels = []
      const tags =
        this.product.featuredVariant.tags?.[this.$i18n.locale] ||
        this.product.featuredVariant.tags

      // TODO: once tags are released 100%, remove this Feature Flag
      if (this.showVariantTagsOnPlp && tags.length) {
        tags.forEach((tag) => labels.push(tag.text))
      }

      return {
        ...this.product,
        label: labels.join(' | ')
      }
    },

    pdpUrl() {
      return (
        this.product.featuredVariant.url ||
        `/products/${this.product.slugUrl}/v/${this.product.featuredVariant.slugUrl}`
      )
    },

    addToBagText() {
      if (this.isAddingToBag) {
        return this.$t('carousel.addingToBag')
      } else {
        return this.$t('carousel.addToBag')
      }
    }
  },

  methods: {
    trackAddItemEvent(lineItem) {
      const data = {
        product_id: lineItem.productId,
        product_name: lineItem.productName,
        variant_id: lineItem.variantId,
        brand: lineItem.brandName,
        price: lineItem.unitPrice,
        quantity: lineItem.quantityValue,
        origin: `homepage${
          this.product.strategy ? `|${this.product.strategy}` : ''
        }`,
        recommendation_id: this.product.recommendationId,
        carousel_name: 'recommended_for_you',
        card_number: this.userCardNumber,
        session_id: this.$cookies.get('_ga_6KSFTBFXP6')?.split('.')[2],
        user_id: this.userId,
        site_country: this.$i18n.countryCode.toLowerCase(),
        traffic_split_key: this.product.splitKey
      }
      this.$analytics.trackEvent('add_to_cart', data)
    },

    openBag() {
      this.$nuxt.$emit('open-cart-dropdown')
    },

    async addToBag() {
      this.isAddingToBag = true
      await this.$store
        .dispatch('cart/createLineItem', this.product.featuredVariant.id)
        .then(() => {
          const lineItem = this.lineItems.find(
            (lineItem) => lineItem.variantId === this.product.featuredVariant.id
          )
          this.trackAddItemEvent(lineItem)
          this.openBag()
        })
        .catch((error) => {
          const errorMessage =
            error.error?.response?.data?.errors?.[0]?.meta?.message ||
            error.error?.message ||
            error.toString()

          this.$notify({
            text: errorMessage,
            type: 'error'
          })
        })
        .finally(() => {
          this.isAddingToBag = false
        })
    },

    async addToWaitlist(waitlistHash) {
      const variantId = waitlistHash.variantId

      if (this.userLoggedIn) {
        try {
          await this.$store.dispatch('waitlist/addToWaitlist', variantId)
        } catch (error) {
          this.$notify({
            text: error,
            type: 'error'
          })
        }
      } else {
        globalThis.location.href = `/sign_in`
      }
    },

    async addToWishlist(wishlistHash) {
      const variantId = wishlistHash.variantId

      if (this.userLoggedIn) {
        try {
          await this.$store.dispatch('wishlist/addToWishlist', variantId)
          this.$notify({
            text: this.$t('wishlist.added', {
              productName: this.product.productName
            })
          })
        } catch (error) {
          this.$notify({
            text: error,
            type: 'error'
          })
        }
      } else {
        globalThis.location.href = `/sign_in`
      }
    }
  }
}
</script>
